import React, { useCallback } from 'react';
import {
  Modal,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@material-ui/core';
import { Button } from '../atoms/ButtonWithAuth';
import { IconButton } from '../atoms/IconButtonWithAuth';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Close, GetApp, Warning } from '@material-ui/icons';
import jaLocale from 'date-fns/locale/ja';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MonthPicker from '../atoms/MonthPickerWithAuth';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    container: {
      background: 'var(--color-white)',
      borderRadius: 10,
      textAlign: 'left',
      width: 460,
      position: 'relative'
    },
    closeButton: {
      color: 'var(--color-white)',
      position: 'absolute',
      fontSize: 20,
      fontWeight: 900,
      top: 10,
      right: 16
    },
    heading: {
      borderRadius: '10px 10px 0 0',
      color: 'var(--color-white)',
      backgroundColor: 'var(--color-admin-key)',
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '29px',
      letterSpacing: '0.5px',
      padding: '20px'
    },
    submitButton: {
      backgroundColor: '#EB5757',
      borderRadius: 999,
      padding: '8px 20px',
      color: 'var(--color-white)',
      '&:hover': {
        backgroundColor: '#ce4040'
      }
    },
    root: {
      width: 'calc(100% - 40px)',
      padding: '0 20px 20px 20px'
    },
    listItem: {
      lineHeight: '44px'
    },
    targetPeriodContainer: {
      background: 'var(--color-white)',
      textAlign: 'left'
    },
    targetPeriodLabel: {
      fontSize: 12,
      color: 'var(--color-gray-3)',
      fontWeight: 500
    },
    button: {
      backgroundColor: '#E0E0E0',
      borderRadius: 999,
      marginLeft: 20,
      padding: '8px 12px 8px'
    },
    buttonsContainer: {
      marginTop: 12,
      textAlign: 'right'
    },
    cancelButton: {
      borderRadius: 999,
      padding: '8px 20px',
      color: 'var(--color-gray-1)',
      backgroundColor: 'var(--color-white)',
      marginRight: 16,
      '&:hover': {
        backgroundColor: '#eee'
      }
    },
    progress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translateY(-50%) translateX(-50%)'
    },
    disabledLink: {
      textDecoration: 'none'
    },
    error: {
      color: '#f44336',
      margin: 0,
      fontSize: '0.75rem',
      minHeight: '1em',
      textAlign: 'left',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      lineHeight: '1em',
      letterSpacing: '0.03333em'
    }
  })
);

interface Props {
  isOpen: boolean;
  setIsOpen: (e: boolean) => void;
  onClickNewInsuranceData: (e: any) => void;
  onClickModifyInsuranceData: (e: any) => void;
  startMonth: string;
  endMonth: string;
  setStartMonth: (e: any) => void;
  setEndMonth: (e: any) => void;
  isInsuranceCsvError: boolean;
  setIsInsuranceCsvError: (e: any) => void;
  insuranceCSVProcessing: boolean;
  setInsuranceCSVProcessing: (value: boolean) => void;
}

const CsvInsuranceExportModal: React.FC<Props> = props => {
  const {
    isOpen,
    setIsOpen,
    onClickNewInsuranceData,
    onClickModifyInsuranceData,
    startMonth,
    endMonth,
    setStartMonth,
    setEndMonth,
    isInsuranceCsvError,
    setIsInsuranceCsvError,
    insuranceCSVProcessing,
    setInsuranceCSVProcessing
  } = props;

  const styles = useStyles({});
  const [startMonthValid, setStartMonthValid] = React.useState(false);
  const [endMonthValid, setEndMonthValid] = React.useState(false);

  // 開始月選択時の処理
  const onSelectStartMonth = React.useCallback(
    (date: Date | null) => {
      setIsInsuranceCsvError(false);
      setEndMonthValid(false);
      if (!date || !moment(date).isValid()) {
        setStartMonth('');
        setStartMonthValid(false);
        return;
      }
      const formattedStart = moment(date).format('YYYY-MM');
      setStartMonth(formattedStart);
      // エラー判定: 開始月 > 終了月
      setStartMonthValid(moment(formattedStart).isAfter(moment(endMonth)));
    },
    [endMonth, setStartMonth, setIsInsuranceCsvError]
  );
  // 終了月選択時の処理
  const onSelectEndMonth = React.useCallback(
    (date: Date | null) => {
      setIsInsuranceCsvError(false);
      setStartMonthValid(false);
      if (!date || !moment(date).isValid()) {
        setEndMonth('');
        setEndMonthValid(false);
        return;
      }
      const formattedEnd = moment(date).format('YYYY-MM');
      setEndMonth(formattedEnd);
      // エラー判定: 開始月 > 終了月
      setEndMonthValid(moment(startMonth).isAfter(moment(formattedEnd)));
    },
    [startMonth, setEndMonth, setIsInsuranceCsvError]
  );

  const resetState = useCallback(() => {
      const thisMonth = moment().format('YYYY-MM'); // リセット後の月は当月固定
      setStartMonth(thisMonth);
      setEndMonth(thisMonth);
      setIsInsuranceCsvError(false);
      setIsOpen(false);
      setStartMonthValid(false);
      setEndMonthValid(false);
    },
    [setEndMonth, setEndMonthValid, setIsInsuranceCsvError, setIsOpen, setStartMonth, setStartMonthValid]
  );

  return (
    <Modal
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      open={isOpen}
      className={styles.modal}
      onClose={() => resetState()}
    >
      <div className={styles.container}>
        <IconButton
          className={styles.closeButton}
          onClick={() => resetState()}
        >
          <Close />
        </IconButton>
        <h2 className={styles.heading}>通信端末修理費用保険CSVエクスポート</h2>
        <div className={styles.root}>
          <List component="nav">
            <ListItem divider={true}>
              <div className={styles.targetPeriodContainer}>
                <p className={styles.targetPeriodLabel}>開始月</p>
                <MuiPickersUtilsProvider locale={jaLocale} utils={DateFnsUtils}>
                  <MonthPicker
                    value={startMonth ? moment(startMonth, 'YYYY-MM').toDate() : null}
                    onChange={onSelectStartMonth}
                    disabled={insuranceCSVProcessing}
                  />
                </MuiPickersUtilsProvider>
                {startMonthValid && (
                  <p className={styles.error}>終了月より前の月を入力してください。</p>
                )}
              </div>
              <div className={styles.targetPeriodContainer}>
                <p className={styles.targetPeriodLabel}>終了月</p>
                <MuiPickersUtilsProvider locale={jaLocale} utils={DateFnsUtils}>
                  <MonthPicker
                    value={endMonth ? moment(endMonth, 'YYYY-MM').toDate() : null}
                    onChange={onSelectEndMonth}
                    disabled={insuranceCSVProcessing}
                  />
                </MuiPickersUtilsProvider>
                {endMonthValid && (
                  <p className={styles.error}>開始月より後の月を入力してください。</p>
                )}
              </div>
            </ListItem>
            <ListItem className={styles.listItem} divider={true}>
              <ListItemText>新規登録データ</ListItemText>
              <IconButton
                edge="end"
                aria-label="download"
                onClick={async e => {
                  setInsuranceCSVProcessing(true);
                  onClickNewInsuranceData(e);
                }}
                disabled={!startMonth || !endMonth || isInsuranceCsvError || insuranceCSVProcessing || startMonthValid || endMonthValid}
              >
                <GetApp />
              </IconButton>
            </ListItem>
            <ListItem className={styles.listItem} divider={true}>
              <ListItemText>解約データ</ListItemText>
              <IconButton
                edge="end"
                aria-label="download"
                onClick={async e => {
                  setInsuranceCSVProcessing(true);
                  onClickModifyInsuranceData(e);
                }}
                disabled={!startMonth || !endMonth || isInsuranceCsvError || insuranceCSVProcessing || startMonthValid || endMonthValid}
              >
                <GetApp />
              </IconButton>
            </ListItem>
          </List>
          <AlertMessage
            open={isInsuranceCsvError}
            level={'アラート'}
            title={'エクスポートできません。'}
            message={'対象期間のCSVデータは作成されていません。'}
          />
          <div className={styles.buttonsContainer}>
            <Button
              className={styles.cancelButton}
              onClick={() => resetState()}
            >
              キャンセル
            </Button>
          </div>
        </div>
        {insuranceCSVProcessing && (
          <div className={styles.progress}>
            <CircularProgress />
          </div>
        )}
      </div>
    </Modal>
  );
};

const useAlertStyles = makeStyles(() =>
  createStyles({
    root: {
      width: 'calc(100% - 32px)',
      height: 50,
      backgroundColor: 'var(--color-error)',
      color: 'var(--color-white)',
      padding: 16,
      margin: '10px 0',
      fontSize: 14
    },
    container: {
      position: 'relative',
      top: -16,
      left: -16
    },
    icon: {
      position: 'absolute',
      top: 16,
      left: 16
    },
    title: {
      position: 'absolute',
      top: 16,
      left: 52
    },
    message: {
      position: 'absolute',
      top: 45,
      left: 52
    }
  })
);

interface P {
  open: boolean;
  level: string;
  title: string;
  message: string;
}

const AlertMessage: React.FC<P> = props => {
  const {
    open,
    title,
    message
  } = props;

  const styles = useAlertStyles();

  return (
    <>
      {open && (
        <div className={styles.root}>
          <div className={styles.container}>
            <div className={styles.icon}>
              <Warning />
            </div>
            <div className={styles.title}>{title}</div>
            <div className={styles.message}>{message}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default CsvInsuranceExportModal;
